import { DEFAULT_AUDIT_PATHS } from "@roomate/core";
import { Link } from "react-router";
import { useCodebaseMap, useMe, useSetWorker, useWorkers, type WorkerSettingDTO } from "../api.ts";
import { Pill, Switch, Tile } from "../calm.tsx";
import { ErrorNote, formatAgo } from "../ui.tsx";

const WORKER_INFO: Record<string, { name: string; blurb: string }> = {
  testgaps: { name: "Test gaps", blurb: "Files a test task when a merge adds code without tests." },
  document: { name: "Documentation", blurb: "Files a docs task when a merge adds exported symbols without doc comments." },
  map: { name: "Codebase map", blurb: "Builds areas, owners and hot files for routing. Files no tasks." },
};

function pathList(paths: readonly string[]): string {
  if (paths.length === 0) return "a configured path";
  if (paths.length === 1) return paths[0]!;
  return `${paths.slice(0, -1).join(", ")} or ${paths[paths.length - 1]}`;
}

/**
 * Known workers get their copy; anything the hub adds later keeps its key instead of throwing.
 * Audit names the paths that actually trigger it (`config.paths`, or the defaults), so an admin
 * override shows up in the row instead of a shortened list.
 */
export function workerInfo(worker: string, paths?: readonly string[]): { name: string; blurb: string } {
  if (worker === "audit") {
    return { name: "Security audit", blurb: `Files a security review when a merge touches ${pathList(paths ?? DEFAULT_AUDIT_PATHS)}.` };
  }
  return WORKER_INFO[worker] ?? { name: worker, blurb: "" };
}

/**
 * Clarification 8: when the hub turns a worker off because the admin who enabled it is no longer
 * an active admin, the row says so. The reason comes from the worker row (`disabledReason`), which
 * the hub clears only when the worker is turned on. A config change that leaves it off keeps the
 * notice. `enabled` wins: a worker that is on is never "re-enable needed".
 */
export function workerReenableLabel(enabled: boolean, disabledReason: string | null | undefined): string | null {
  if (enabled || disabledReason == null) return null;
  return disabledReason ? `re-enable needed · ${disabledReason}` : "re-enable needed";
}

function lastRun(ts: number | null): string {
  if (!ts) return "never";
  return formatAgo(Date.now() - ts);
}

export function WorkersCard({ projectId }: { projectId: string }) {
  const me = useMe().data;
  const workers = useWorkers(projectId);
  const isAdmin = me?.access === "admin";
  const mapOn = !!workers.data?.find((w) => w.worker === "map")?.enabled;

  return (
    <>
      <Tile span={6} title="Workers" aside="off by default · file tasks, never edit code" testId="workers-card">
        {workers.error ? (
          <ErrorNote error={workers.error} />
        ) : !workers.data ? (
          <p className="text-[13px] text-ink-3">Loading…</p>
        ) : (
          <ul className="divide-y divide-rule">
            {workers.data.map((w) => (
              <WorkerRow key={w.worker} w={w} projectId={projectId} isAdmin={isAdmin} reenable={workerReenableLabel(w.enabled, w.disabledReason)} />
            ))}
          </ul>
        )}
      </Tile>
      {mapOn && <MapPanel projectId={projectId} />}
    </>
  );
}

function WorkerRow({ w, projectId, isAdmin, reenable }: { w: WorkerSettingDTO; projectId: string; isAdmin: boolean; reenable: string | null }) {
  // One mutation per row: a shared mutation only remembers the latest call, so an earlier toggle
  // becomes clickable again while its PUT is still in flight and its error is dropped.
  const set = useSetWorker(projectId);
  const info = workerInfo(w.worker, w.config.paths);
  const filesTasks = w.worker !== "map";
  return (
    <li data-testid={`worker-row-${w.worker}`} className="py-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="min-w-[220px] flex-1">
          <p className="text-[14px] font-medium leading-snug">{info.name}</p>
          {info.blurb && <p className="text-[12.5px] text-ink-2">{info.blurb}</p>}
          {reenable && (
            <p className="mt-0.5 text-[12.5px] font-medium text-signal" data-testid={`worker-reenable-${w.worker}`}>
              {reenable}
            </p>
          )}
        </div>
        <Switch
          checked={w.enabled}
          onChange={(enabled) => set.mutate({ worker: w.worker, enabled })}
          disabled={!isAdmin}
          busy={set.isPending}
          title={isAdmin ? undefined : "Only admins can turn workers on or off"}
          label={<span className="sr-only">{`${info.name} worker`}</span>}
          testId={`worker-toggle-${w.worker}`}
        />
        <span className="min-w-[92px] text-[12.5px] text-ink-3" data-testid={`worker-last-${w.worker}`}>
          Last run {lastRun(w.lastRunAt)}
        </span>
        {filesTasks && (
          <Link
            to={`/projects/${projectId}?label=${encodeURIComponent(`worker:${w.worker}`)}&since=${w.filedSince}`}
            data-testid={`worker-filed-${w.worker}`}
            className="text-[12.5px] text-ink-2 underline decoration-rule hover:text-ink"
          >
            {w.filed7d} filed in 7d
          </Link>
        )}
        {filesTasks && (
          <span className="text-[12.5px] text-ink-3" data-testid={`worker-cap-${w.worker}`}>
            cap {w.config.dailyCap}/day
          </span>
        )}
      </div>
      <ErrorNote error={set.error} />
    </li>
  );
}

function MapPanel({ projectId }: { projectId: string }) {
  const map = useCodebaseMap(projectId);
  const data = map.data;
  return (
    <Tile span={6} title="Codebase map" aside={data ? `built ${formatAgo(Date.now() - data.builtAt)}` : undefined} testId="map-panel">
      {map.error ? (
        <ErrorNote error={map.error} />
      ) : data === undefined ? (
        <p className="text-[13px] text-ink-3">Loading…</p>
      ) : data === null ? (
        <p className="text-[13px] text-ink-3" data-testid="map-empty">
          The map hasn't been built yet. It appears after the next sweep, once a node has reported this checkout.
        </p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="min-w-0">
            <h3 className="mb-2 text-[12.5px] font-medium text-ink-2">Areas</h3>
            <table className="w-full text-left text-[12.5px]" data-testid="map-areas">
              <thead className="text-ink-3">
                <tr>
                  <th className="py-1 pr-3 font-normal">Area</th>
                  <th className="py-1 pr-3 font-normal">Owners</th>
                  <th className="py-1 font-normal">Allowed agents</th>
                </tr>
              </thead>
              <tbody>
                {data.areas.map((a) => (
                  <tr key={a.area} data-testid="map-area" className="border-t border-rule align-top">
                    <td className="py-1.5 pr-3 font-mono">{a.area}</td>
                    <td className="py-1.5 pr-3">{a.owners.length ? a.owners.map((o) => `${o.name || o.actorId} (${o.merged})`).join(", ") : <span className="text-ink-3">—</span>}</td>
                    <td className="py-1.5">{a.allowedAgents.length ? a.allowedAgents.map((o) => o.name || o.id).join(", ") : <span className="text-ink-3">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="min-w-0">
            <h3 className="mb-2 text-[12.5px] font-medium text-ink-2">Hot files (30 days)</h3>
            {data.hotFiles.length === 0 ? (
              <p className="text-[13px] text-ink-3">No churn reported yet.</p>
            ) : (
              <ol className="space-y-1" data-testid="map-hot-files">
                {data.hotFiles.slice(0, 20).map((f) => (
                  <li key={f.path} data-testid="map-hot-file" className="flex items-center justify-between gap-3 text-[12.5px]">
                    <span className="min-w-0 truncate font-mono">{f.path}</span>
                    <Pill>{f.commits}</Pill>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </Tile>
  );
}
